import { routeTree } from '@/routeTree.gen.ts';
import { useAuthStore } from '@/stores/auth-store';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { QueryClient } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: () => <LoadingSpinner />,
  context: { auth: undefined!, queryClient: undefined! },
});

export function AppRouterProvider({ queryClient }: { queryClient: QueryClient }) {
  // Stable context reference — auth is a getter so the object never changes between renders
  const context = useMemo(() => ({ auth: useAuthStore.getState, queryClient }), [queryClient]);

  // When auth transitions from authenticated → unauthenticated after initialization
  // (e.g. 401 in another tab, or refresh failure), re-run all active beforeLoad hooks
  // so _auth.tsx guard redirects to /login.
  useEffect(() => {
    return useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, wasAuthenticated) => {
        if (wasAuthenticated && !isAuthenticated) {
          void router.invalidate();
        }
      }
    );
  }, []);

  return <RouterProvider router={router} context={context} />;
}
